import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatShortDate } from '@/lib/format';
import type { SeriesPoint } from '@/types';

export interface MetricsChartProps {
    data: SeriesPoint[];
    loading?: boolean;
    height?: number;
}

/** Clicks vs signups over the selected period (FR-24). */
export default function MetricsChart({ data, loading = false, height = 260 }: MetricsChartProps) {
    if (loading) {
        return <div className="skeleton-shimmer w-full rounded-xl" style={{ height }} />;
    }

    const hasData = data.some((point) => point.clicks > 0 || point.signups > 0);

    return (
        <div className="rounded-xl border border-[#E0E0E0] bg-white p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-[15px] font-bold text-secondary">Clicks &amp; signups</h3>
                <div className="flex items-center gap-4 text-[12px] text-[#747474]">
                    <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-[#151515]" />
                        Link clicks
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-primary" />
                        Signups
                    </span>
                </div>
            </div>

            {hasData ? (
                <div className="mt-4">
                    <ResponsiveContainer width="100%" height={height}>
                        <AreaChart data={data} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                            <defs>
                                <linearGradient id="clicksFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#151515" stopOpacity={0.18} />
                                    <stop offset="95%" stopColor="#151515" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="signupsFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ff6600" stopOpacity={0.25} />
                                    <stop offset="95%" stopColor="#ff6600" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#9a9a9a', fontSize: 11 }}
                                tickFormatter={formatShortDate}
                                minTickGap={24}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#9a9a9a', fontSize: 11 }}
                                allowDecimals={false}
                            />
                            <Tooltip
                                labelFormatter={(label) => formatShortDate(String(label))}
                                contentStyle={{
                                    borderRadius: 10,
                                    border: '1px solid #eee',
                                    fontSize: 13,
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="clicks"
                                name="Link clicks"
                                stroke="#151515"
                                strokeWidth={2}
                                fill="url(#clicksFill)"
                            />
                            <Area
                                type="monotone"
                                dataKey="signups"
                                name="Signups"
                                stroke="#ff6600"
                                strokeWidth={2}
                                fill="url(#signupsFill)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div
                    className="mt-4 flex items-center justify-center rounded-lg bg-[#fafafa] text-[13px] text-[#9a9a9a]"
                    style={{ height }}
                >
                    No activity in this period yet
                </div>
            )}
        </div>
    );
}
